import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Braces, Play } from "lucide-react";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Button } from "@tubo/ui/components/ui/button";
import { Input } from "@tubo/ui/components/ui/input";
import { Label } from "@tubo/ui/components/ui/label";
import { useQueryIntent } from "@tubo/api-models/hooks/knowledge-debug-api.hooks";
import type { QueryIntentDebugResp } from "@tubo/api-models/hooks/knowledge-debug-api.schemas";
import { useSecondaryNavTitle } from "@client/components/secondary-nav";
import { useUiTranslation } from "@tubo/ui/i18n";

export const Route = createFileRoute("/_app/semantic-platform/knowledge-debug/")({
  component: KnowledgeDebugPage,
});

const SAMPLE_KEYS = ["sample1", "sample2", "sample3", "sample4"] as const;

/**
 * 查询意图调试页(波次5 5A-1,KS-03):结构化展示意图/匹配原因/歧义候选/解析来源。
 * 仅调试入口,不进生产 chat;LLM 兜底需租户默认模型可用。
 */
function KnowledgeDebugPage() {
  const { t } = useUiTranslation("semanticPlatform");
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState<QueryIntentDebugResp | null>(null);
  const queryIntent = useQueryIntent({
    mutation: {
      onSuccess: (resp) => setResult(resp),
      onError: () => toast.error(t("knowledgeDebug.parseFailed")),
    },
  });

  const run = (q: string) => {
    if (!q.trim()) return;
    queryIntent.mutate({ data: { question: q.trim() } });
  };

  // 头部注入域级 SecondaryNav,页面体不再自写标题栏
  const headerTitle = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Braces className="size-4 text-primary" />
        <span className="text-sm font-semibold">{t("knowledgeDebug.title")}</span>
      </div>
    ),
    [t],
  );
  useSecondaryNavTitle(headerTitle);

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-4 overflow-auto p-4">
        <div className="flex items-end gap-2">
          <div className="grid flex-1 gap-1.5">
            <Label>{t("knowledgeDebug.question")}</Label>
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void run(question)}
              placeholder={t("knowledgeDebug.placeholder")}
            />
          </div>
          <Button size="sm" disabled={!question.trim() || queryIntent.isPending} onClick={() => void run(question)}>
            <Play className="mr-1 size-4" /> {queryIntent.isPending ? t("knowledgeDebug.running") : t("knowledgeDebug.run")}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {SAMPLE_KEYS.map((key) => {
            const sample = t(`knowledgeDebug.${key}`);
            return (
            <Button key={key} variant="outline" size="sm" onClick={() => { setQuestion(sample); void run(sample); }}>
              {sample}
            </Button>
          ); })}
        </div>

        {result && (
          <div className="space-y-3 rounded-lg border bg-surface/50 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">queryType: {result.intent.queryType}</Badge>
              <Badge variant={result.confident ? "default" : "secondary"}>{result.confident ? t("knowledgeDebug.confident") : t("knowledgeDebug.lowConfidence")}</Badge>
              <Badge variant="secondary">{t("knowledgeDebug.source")}: {result.source}</Badge>
              {result.intent.operator && <Badge variant="outline">operator: {result.intent.operator}</Badge>}
              {result.intent.standardUnit && (
                <Badge variant="outline">
                  {result.intent.rawUnit ?? ""} → {result.intent.standardUnit}
                  {result.intent.standardValue !== undefined ? ` (${result.intent.standardValue})` : ""}
                </Badge>
              )}
              {result.aggregation && <Badge variant="outline">{t("knowledgeDebug.aggregation")}: {result.aggregation}</Badge>}
            </div>

            <div className="text-sm">
              <span className="text-muted-foreground">{t("knowledgeDebug.conceptHits")}</span>{" "}
              {result.matchedConceptTerms.length === 0 ? (
                <span className="text-muted-foreground">{t("knowledgeDebug.none")}</span>
              ) : (
                result.matchedConceptTerms.map((t) => (
                  <Badge key={`${t.term}-${t.conceptCode}`} variant="outline" className="mr-1">
                    {t.term} → {t.conceptCode}({t.matchType})
                  </Badge>
                ))
              )}
            </div>

            {result.ambiguityCandidates.length > 0 && (
              <div className="text-sm text-amber-600">
                {t("knowledgeDebug.ambiguityCandidates")}
                {result.ambiguityCandidates.map((a) => (
                  <span key={a.term} className="ml-2">「{a.term}」→ {a.conceptCodes.join(" / ")}</span>
                ))}
              </div>
            )}

            {result.warnings.length > 0 && (
              <div className="text-xs text-muted-foreground">{t("knowledgeDebug.warning")}{result.warnings.join(";")}</div>
            )}

            <pre className="max-h-64 overflow-auto rounded bg-muted/40 p-2 text-xs">{JSON.stringify(result.intent, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
}
